<template>
    <div class="quiz-container">
        <headerPage />

        <!-- 试卷标题 -->
        <div class="quiz-header">
            <h2>{{ examTitle }}</h2>
            <p>共 {{ questionList.length }} 题</p>
        </div>

        <div class="quiz-content">
            <!-- 左侧试题区域 -->
            <div class="question-area">
                <el-card v-for="(question, index) in questionList" :key="index" class="question-card">
                    <div class="question-title">
                        <span>
                            {{ index + 1 }}. {{ question.text }}
                            <span v-if="question.type === 'multiple'">(多选)</span>
                        </span>
                    </div>

                    <!-- 选项区域 -->
                    <div class="question-options">
                        <!-- 单选题、判断题 -->
                        <el-radio-group v-if="question.type === 'single' || question.type === 'truefalse'"
                            v-model="question.userAnswer" @change="onAnswerChange(question)">
                            <el-radio v-for="(option) in question.options" :key="option.value" :label="option.value"
                                class="option-item">
                                {{ option.value }}. {{ option.label }}
                            </el-radio>
                        </el-radio-group>

                        <!-- 多选题 -->
                        <el-checkbox-group v-else-if="question.type === 'multiple'" v-model="question.userAnswer"
                            @change="onAnswerChange(question)">
                            <el-checkbox v-for="(option) in question.options" :key="option.value" :label="option.value"
                                class="option-item">
                                {{ option.value }}. {{ option.label }}
                            </el-checkbox>
                        </el-checkbox-group>
                    </div>
                </el-card>
            </div>

            <!-- 右侧答题卡 -->
            <div class="question-card-panel">
                <!-- 倒计时显示 -->
                <div v-if="timeLeft > 0" class="countdown">
                    剩余时间：<span class="time-text">{{ formattedTime }}</span>
                </div>

                <!-- 顶部标题和折叠按钮 -->
                <div class="card-header">
                    <span class="card-title">答题卡</span>
                    <el-button type="text" size="mini" @click="toggleCardCollapse">
                        {{ isCardCollapsed ? "展开答题卡" : "收起答题卡" }}
                    </el-button>
                </div>

                <!-- 中间题号区域 -->
                <transition name="el-fade-in">
                    <div v-show="!isCardCollapsed" class="question-nav">
                        <el-button v-for="(q, idx) in questionList" :key="idx" @click="scrollToQuestion(idx)"
                            :class="{ answered: hasAnswered(q) }" type="default" size="mini">
                            {{ idx + 1 }}
                        </el-button>
                    </div>
                </transition>

                <!-- 底部提交按钮 -->
                <div style="margin-top: 15px; text-align: center;">
                    <el-button type="primary" @click="submitAnswers">立即提交</el-button>
                </div>
            </div>
        </div>

        <bottomPage />
    </div>
</template>

<script>
import headerPage from "@/components/header/header";
import bottomPage from "@/components/bottom/bottom";
import * as PaperApi from "@/api/paper";

export default {
    components: {
        headerPage,
        bottomPage,
    },
    data() {
        return {
            limitTime: 0,
            paperId: 0,
            examTitle: "",
            timeLeft: 0,
            questionList: [],
            isCardCollapsed: false,
            loadingInstance: null,
            timer: null, // ✅ 新增：用于倒计时清理
        };
    },
    computed: {
        formattedTime() {
            const minutes = Math.floor(this.timeLeft / 60);
            const seconds = this.timeLeft % 60;
            return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
        },
    },
    created() {
        this.loadingInstance = this.$loading({
            lock: true,
            text: "正在加载试题，请稍候...",
            spinner: "el-icon-loading",
            background: "rgba(0, 0, 0, 0.4)",
        });
        // ✅ 改动1：调用缓存加载逻辑（而不是直接加载试卷）
        this.tryRestoreOrLoadPaper();
    },
    methods: {
        toggleCardCollapse() {
            this.isCardCollapsed = !this.isCardCollapsed;
        },
        scrollToQuestion(index) {
            const element = document.querySelectorAll(".question-card")[index];
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }

            this.saveProgress();
        },
        hasAnswered(question) {
            return Array.isArray(question.userAnswer)
                ? question.userAnswer.length > 0
                : question.userAnswer !== "";
        },
        // ✅ 改动2：主入口逻辑 - 优先尝试从缓存还原
        async tryRestoreOrLoadPaper() {
            const userId = this.$store.getters.userId;
            const cacheKey = `quiz_cache_${this.$route.params.paperId}_${userId}`;
            const cachedStr = localStorage.getItem(cacheKey);
            let cached = null;

            if (cachedStr) {
                cached = JSON.parse(cachedStr);
                const now = Date.now();
                if (now - cached.saveTime > 3600 * 1000) { // ⏳ 一小时过期
                    localStorage.removeItem(cacheKey);
                    cached = null;
                }
            }

            if (cached) {
                // ✅ 完全从缓存恢复
                this.paperId = cached.paperId;
                this.examTitle = cached.examTitle;
                this.limitTime = cached.limitTime;
                this.timeLeft = cached.timeLeft;
                this.isCardCollapsed = cached.isCardCollapsed;
                this.questionList = cached.questionList;
                if (this.timeLeft > 0) this.startCountdown();
            } else {
                // 无缓存 -> 正常加载接口
                await this.loadPaper();
            }

            if (this.loadingInstance) {
                this.loadingInstance.close();
            }

            // 加载完成后立即缓存一次
            if (this.questionList.length > 0) {
                this.saveProgress();
            }
        },
        async loadPaper() {
            try {
                const res = await PaperApi.getQuizPaperQuestion(
                    this.$route.params.paperId
                );
                if (res.code !== 0 || !res.data) return;

                const data = res.data;
                this.paperId = data.paperDetail.id;
                this.examTitle = data.paperDetail.paperName;

                if (data.paperDetail.limitTime > 0) {
                    this.limitTime = data.paperDetail.limitTime * 60;
                    this.timeLeft = data.paperDetail.limitTime * 60;
                    this.startCountdown();
                }

                const formatQuestion = (q, type) => {
                    const base = {
                        id: q.id,
                        text: q.title,
                        type,
                        questionType: q.questionType,
                        score: q.score,
                        correct: q.correct,
                        userAnswer: type === "multiple" ? [] : "",
                        explanation: q.analysis,
                        showExplanation: false,
                    };
                    if (type === "single" || type === "multiple") {
                        const options = [];
                        ["A", "B", "C", "D", "E"].forEach((letter) => {
                            const key = `answer${letter}`;
                            if (q[key]) {
                                options.push({ value: letter, label: q[key] });
                            }
                        });
                        base.options = options;
                    }
                    return base;
                };

                const single = data.onlyQuestionList.map((q) =>
                    formatQuestion(q, "single")
                );
                const multiple = data.mulQuestionList.map((q) =>
                    formatQuestion(q, "multiple")
                );
                const judge = data.judgeQuestionList.map((q) => ({
                    id: q.id,
                    text: q.title,
                    type: "truefalse",
                    questionType: q.questionType,
                    score: q.score,
                    correct: q.correct,
                    userAnswer: "",
                    explanation: q.analysis,
                    showExplanation: false,
                    options: [
                        { value: "A", label: "正确" },
                        { value: "B", label: "错误" },
                    ],
                }));

                this.questionList = [...single, ...multiple, ...judge];
            } finally {
                if (this.loadingInstance) {
                    this.loadingInstance.close();
                }
            }
        },
        // 用户选择答案时触发
        onAnswerChange(question) {
            this.saveProgress();
        },
        async submitAnswers() {
            const answerList = this.questionList.map((q) => {
                const userAnswer = Array.isArray(q.userAnswer)
                    ? q.userAnswer.sort().join("")
                    : q.userAnswer;
                
                const doRight = userAnswer === q.correct ? 0 : 1;

                return {
                    questionId: q.id,
                    questionType: q.questionType,
                    userAnswer,
                    doRight,
                    customerScore: doRight === 0 ? q.score : 0,
                };
            });

            const req = {
                doTime: this.limitTime > 0 ? this.limitTime - this.timeLeft : 0,
                createStudent: this.$store.getters.userId,
                examPaperId: this.paperId,
                acquireScore: answerList.reduce((sum, item) => sum + item.customerScore, 0),
                answerReqList: answerList,
            };

            const resp = await PaperApi.createQuizPaperAnswer(req);
            this.$message.success("答卷已提交！");

            // ✅ 改动3：提交后清除缓存
            this.clearProgress();

            if (resp.code !== 0) return;

            this.$router.back();
        },
        startCountdown() {
            this.timer = setInterval(() => {
                if (this.timeLeft > 0) {
                    this.timeLeft--;
                } else {
                    clearInterval(this.timer);
                    this.submitAnswers();
                }
            }, 1000);
        },
        // ✅ 改动5：新增保存进度方法
        saveProgress() {
            const userId = this.$store.getters.userId;
            const cacheKey = `quiz_cache_${this.$route.params.paperId}_${userId}`;
            const questionsSnapshot = this.questionList.map(q => ({
                id: q.id,
                text: q.text,
                type: q.type,
                questionType: q.questionType,
                score: q.score,
                correct: q.correct,
                userAnswer: q.userAnswer,
                explanation: q.explanation,
                showExplanation: q.showExplanation,
                options: q.options,
            }));

            const cacheData = {
                paperId: this.paperId,
                examTitle: this.examTitle,
                limitTime: this.limitTime,
                timeLeft: this.timeLeft,
                isCardCollapsed: this.isCardCollapsed,
                questionList: questionsSnapshot,
                saveTime: Date.now(),
            };

            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        },

        // ✅ 改动6：新增清除缓存方法
        clearProgress() {
            const userId = this.$store.getters.userId;
            const cacheKey = `quiz_cache_${this.$route.params.paperId}_${userId}`;
            localStorage.removeItem(cacheKey);
        },
    },
    beforeDestroy() {
        clearInterval(this.timer);
    },
};
</script>

<style scoped>
.quiz-container {
    background-color: #f4f4f4;
    min-height: 100vh;
    padding: 20px;
}

.quiz-header {
    background-color: #ffffff;
    padding: 15px;
    text-align: center;
    font-size: 18px;
    font-weight: bold;
    border-bottom: 1px solid #e0e0e0;
}

.quiz-content {
    display: flex;
    max-width: 1200px;
    margin: 20px auto;
    gap: 20px;
}

.question-area {
    flex: 2;
}

.question-card {
    background-color: #f9f9f9;
    margin-bottom: 15px;
    padding: 15px;
    border-radius: 5px;
}

.question-title {
    font-weight: bold;
    margin-bottom: 10px;
}

.question-options {
    margin-top: 10px;
}

.option-item {
    display: block;
    margin: 6px 0;
}

.question-card-panel {
    min-width: 220px;
    flex: 0;
    background: #fff;
    padding: 10px;
    border-radius: 8px;
    border: 1px solid #eee;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    position: sticky;
    top: 20px;
    height: fit-content;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.card-title {
    font-weight: bold;
}

.question-nav {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
    max-height: 230px;
    overflow-y: auto;
    padding: 5px;
}

.question-nav button {
    width: 36px;
    height: 36px;
    font-size: 13px;
    text-align: center;
    justify-content: center;
    align-items: center;
    display: flex;
    padding: 0;
    border-radius: 4px;
    border: 1px solid #dcdfe6;
    background-color: #fff;
    transition: 0.2s;
    margin: 0;
}

.answered {
    background-color: #409eff !important;
    color: white;
}

.countdown {
    font-size: 14px;
    color: #f56c6c;
    font-weight: bold;
    text-align: center;
    margin-bottom: 10px;
    padding: 6px;
    border-radius: 4px;
    background-color: #fff7f7;
    border: 1px solid #fbc4c4;
}

.time-text {
    font-size: 16px;
}
</style>
